import * as THREE from 'three';
import { OBJLoader } from 'three/addons/loaders/OBJLoader.js';

/**
 * Loads the female head OBJ model and applies transparent glass-like material.
 * The model is centered, scaled, and rotated to match the scene coordinate system.
 */
export async function createHeadModel(): Promise<THREE.Group> {
    const group = new THREE.Group();
    const loader = new OBJLoader();

    const obj = await loader.loadAsync('/models/11091_FemaleHead_v4.obj');

    // 3ds Max: Z-up, face toward +Y. Three.js: Y-up, face toward +Z.
    // Euler XYZ: Rx(-PI/2) converts Z-up → Y-up,
    //            Rz(PI) flips X/Y so the face (at +old_Y) ends up at +Z.
    obj.rotation.x = -Math.PI / 2;
    obj.rotation.z = Math.PI;
    obj.updateMatrixWorld(true);

    // Compute bounding box after rotation
    const box = new THREE.Box3().setFromObject(obj);
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());

    // Scale to fit ~2.2 units height (matching previous skull proportions)
    const targetHeight = 2.2;
    const scale = targetHeight / size.y;

    // ── Materials ──────────────────────────────
    // Front-face glass material
    const glassMat = new THREE.MeshPhysicalMaterial({
        color: 0xc8d8e8,
        transparent: true,
        opacity: 0.18,
        roughness: 0.3,
        metalness: 0.05,
        clearcoat: 0.8,
        clearcoatRoughness: 0.15,
        side: THREE.FrontSide,
        depthWrite: false,
    });

    // Back-face material (subtle inner surface)
    const backMat = new THREE.MeshPhysicalMaterial({
        color: 0x8899aa,
        transparent: true,
        opacity: 0.06,
        roughness: 0.5,
        metalness: 0.0,
        side: THREE.BackSide,
        depthWrite: false,
    });

    // Wireframe overlay for medical/schematic look
    const wireMat = new THREE.MeshBasicMaterial({
        color: 0x5599bb,
        wireframe: true,
        transparent: true,
        opacity: 0.04,
    });

    // Apply materials to all meshes in the model
    const meshesToAdd: THREE.Mesh[] = [];

    obj.traverse((child) => {
        if (child instanceof THREE.Mesh) {
            // Main glass material
            child.material = glassMat;

            // Create back-face clone
            const backMesh = new THREE.Mesh(child.geometry, backMat);
            backMesh.position.copy(child.position);
            backMesh.rotation.copy(child.rotation);
            backMesh.scale.copy(child.scale);
            meshesToAdd.push(backMesh);

            // Create wireframe clone
            const wireMesh = new THREE.Mesh(child.geometry, wireMat);
            wireMesh.position.copy(child.position);
            wireMesh.rotation.copy(child.rotation);
            wireMesh.scale.copy(child.scale);
            meshesToAdd.push(wireMesh);
        }
    });

    // Add clones after traversal to avoid modifying during iteration
    for (const mesh of meshesToAdd) {
        obj.add(mesh);
    }

    // Create a wrapper group for centering and scaling
    const wrapper = new THREE.Group();
    wrapper.add(obj);
    wrapper.scale.setScalar(scale);
    // Offset so the model's center lands at the origin
    wrapper.position.set(
        -center.x * scale,
        -center.y * scale,
        -center.z * scale,
    );

    group.add(wrapper);
    return group;
}
